import { authorizedRequest } from "../../../services/apiClient";
import type { UserProfilePhoto } from "../../../types/auth";

export interface SupportParticipant {
  user_id: string;
  role: "customer" | "support";
  state: string;
  name?: string;
  email?: string;
  profile_photo?: UserProfilePhoto | null;
}

export interface SupportConversation {
  id: string;
  kind: string;
  status: string;
  last_message_at?: string;
  participants: SupportParticipant[];
  unread_count?: number;
}

export interface SupportMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_role: "customer" | "support" | "system";
  content: string;
  created_at: string;
  delivery_state: string;
  redaction_reason?: string | null;
}

export interface SupportMessagesResponse {
  messages: SupportMessage[];
}

export async function fetchSupportConversation(
  accessToken: string
): Promise<SupportConversation> {
  return authorizedRequest<SupportConversation>(
    accessToken,
    "/support/conversation"
  );
}

export async function fetchSupportMessages(
  accessToken: string,
  conversationId: string,
  after?: string,
  limit = 50
): Promise<SupportMessagesResponse> {
  const search = new URLSearchParams();
  if (after) search.set("after", after);
  if (limit) search.set("limit", `${limit}`);
  return authorizedRequest<SupportMessagesResponse>(
    accessToken,
    `/support/conversations/${conversationId}/messages?${search.toString()}`
  );
}

export async function sendSupportMessage(
  accessToken: string,
  conversationId: string,
  content: string
): Promise<SupportMessage> {
  return authorizedRequest<SupportMessage>(
    accessToken,
    `/support/conversations/${conversationId}/messages`,
    {
      method: "POST",
      body: JSON.stringify({ content })
    }
  );
}

export async function markSupportRead(
  accessToken: string,
  conversationId: string,
  lastReadMessageId?: string
): Promise<void> {
  await authorizedRequest(
    accessToken,
    `/support/conversations/${conversationId}/read`,
    {
      method: "POST",
      body: JSON.stringify({
        lastReadMessageId: lastReadMessageId ?? null
      })
    }
  );
}

export async function fetchSupportConversationsAdmin(
  accessToken: string,
  limit = 20,
  offset = 0
): Promise<{ conversations: SupportConversation[]; total: number; limit: number; offset: number; total_unread?: number }> {
  const search = new URLSearchParams();
  search.set("limit", `${limit}`);
  search.set("offset", `${offset}`);
  return authorizedRequest(
    accessToken,
    `/admin/support/conversations?${search.toString()}`
  );
}
